import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import DashboardLayout from '../../../components/layout/layout';
import {
  Box,
  Button,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox, Skeleton, Link,
  FormControlLabel,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DesktopWindowsOutlinedIcon from '@mui/icons-material/DesktopWindowsOutlined';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useGetAllCompanies } from '../../../hooks/api/user';
import { ACTIVE_COLOR, INACTIVE_COLOR } from '../../../components/ui/formInput';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  backgroundColor: '#f2f2f2',
  marginBottom: theme.spacing(1),
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
  borderBottomLeftRadius: '4px',
  borderBottomRightRadius: '4px',
  boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.1), inset 0 0 5px rgba(255, 255, 255, 0.6)',
  '&:before': {
    position: 'relative !important',
  }
}));

const CheckboxUI = styled(Checkbox)(({ theme }) => ({
  '&.Mui-checked': {
    color: '#c3183c',
  },
}));

type SortField = 'id' | 'name' | 'needUsers' | 'type' | 'custNum' | 'spNum' | 'verifyDate';
type SortDirection = 'asc' | 'desc' | null;
type SortState = { field: SortField | null; direction: SortDirection };

export interface CPlist {
  id: string;
  name: string;
  needUsers: boolean;
  type: string;
  custNum: string;
  spNum: string;
  verifyDate: string;
  oldName: string;
  dateAdded: string;
  numLogons:string;
  lastLogon:string;
  verifiedBy:string;
}

const ManageChannelPartner: FunctionComponent = () => {
  const navigate = useNavigate();
  // Removed pagination state variables
  const [expanded, setExpanded] = useState<string | false>(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>(['Show All']);
  const [ CP_Safe, setCP_Safe ] = useState<CPlist[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const roles = [
    { label: 'Admins', key: 'admin' },
    { label: 'Up-loaders', key: 'upload' },
    { label: 'Orders', key: 'enterOrders' },
    { label: 'Dashboard', key: 'dashboard' },
    { label: 'Forecast', key: 'forecast' },
  ];
  const { data: CP, isFetched: hasFetched, isLoading } = useGetAllCompanies();
    
  useEffect(() => {
    setCP_Safe(CP ?? []);
  }, [CP]);

  // Sorting state
  const [sortState, setSortState] = useState<SortState>({ field: null, direction: null });

  // Filter CP by name
  const filteredCP = useMemo(() => {
    const list = CP || CP_Safe || [];
    if (!searchTerm.trim()) {
      return list;
    }
    
    const searchLower = searchTerm.toLowerCase().trim();
    return list.filter((cp: CPlist) => {
      const name = (cp.name || '').toLowerCase();
      return name.includes(searchLower);
    });
  }, [CP, CP_Safe, searchTerm]);

  // Sorting logic (removed pagination)
  const sortedCP = useMemo(() => {
    const list = filteredCP;
    if (!sortState.field || !sortState.direction) return list;
    return [...list].sort((a, b) => {
      let aValue: any = a[sortState.field!];
      let bValue: any = b[sortState.field!];
      
      // Handle null values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';
      
      // Handle date fields
      if (sortState.field === 'verifyDate') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      
      // Handle boolean fields
      if (typeof aValue === 'boolean') {
        aValue = aValue ? 1 : 0;
        bValue = bValue ? 1 : 0;
      }
      
      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortState.direction === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [filteredCP, sortState]);

  // Sort handler
  const handleSort = (field: SortField) => {
    setSortState(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };
  // Sort icon
  const getSortIcon = (field: SortField) => {
    if (sortState.field !== field) return <UnfoldMoreIcon fontSize="small" />;
    if (sortState.direction === 'asc') return <ArrowUpwardIcon fontSize="small" />;
    if (sortState.direction === 'desc') return <ArrowDownwardIcon fontSize="small" />;
    return <UnfoldMoreIcon fontSize="small" />;
  };

  const handleRoleChange = (role: string) => {
    setSelectedRoles([role]);
  };

  const handleDoneClick = () => {
    const query = selectedRoles.join(',');
    navigate(`/otherextranet/managecp/user-list/${query}`);
  };

  const handleAccordionChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    if (panel === 'upload'){
      navigate(`/otherextranet/managecp/upload-date`);
    }

    if (panel === 'userlist'){
      setExpanded(isExpanded ? panel : false);
    }
  };

  // Handle search term change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  return (
    <DashboardLayout
      titleIcon={<DesktopWindowsOutlinedIcon />}
      title="Other Extranet"
      initialBreadcrumbs="Extranet/Barksdale Admin"
      levelBreadcrumbs=""
      breadcrumbs="Channel Partner List"
      activeMenu="overview"
      containerTitle={"Manage Channel Partners"}
    >
      <Box sx={{ pt: 0, px: 0, pb: 0 }}>
        <Box mb={2}>
          <Typography variant="body1" gutterBottom sx={{ fontSize: '1rem' }}>
            Inventory
          </Typography>
        </Box>

        <Box
          sx={{
            border: '1px solid #ccc',
            borderRadius: 2,
            p: 2,
            mb: 3,
            backgroundColor: '#ffffff'
          }}
        >
          <StyledAccordion expanded={expanded === 'upload'} onChange={handleAccordionChange('upload')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontSize: '0.9rem' }}>Upload Dates</Typography>
            </AccordionSummary>
          </StyledAccordion>

          <StyledAccordion expanded={expanded === 'userlist'} onChange={handleAccordionChange('userlist')} sx={{ mt: 4 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>User List</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <FormControlLabel
                    control={
                      <CheckboxUI
                        checked={selectedRoles.includes('showAll')}
                        onChange={() => handleRoleChange('showAll')}
                      />
                    }
                    label={'Show All'}
                  />
                </Box>

                <Box display="flex" alignItems="center" justifyContent={'space-between'} gap={4} flexWrap="wrap">
                  <Box display="flex" alignItems="center" gap={4} flexWrap="wrap">
                    {roles.map(({ label, key }) => (
                      <Box key={key} display="flex" alignItems="center" gap={1}>
                        <FormControlLabel
                          control={
                            <CheckboxUI
                              checked={selectedRoles.includes(key)}
                              onChange={() => handleRoleChange(key)}
                            />
                          }
                          label={label}
                        />
                      </Box>
                    ))}
                  </Box>

                  <Button
                    variant="contained"
                    sx={{ backgroundColor: '#c3183c', height: 30, px: 3, whiteSpace: 'nowrap', minWidth: 80 }}
                    onClick={handleDoneClick}
                  >
                    DONE
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </StyledAccordion>
        </Box>

        {/* Search Box */}
        <Box sx={{ mt: 5, mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <TextField
            placeholder="Search by name"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: searchTerm ? ACTIVE_COLOR : INACTIVE_COLOR }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClearSearch}
                    edge="end"
                    size="small"
                    sx={{
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: 'rgba(195, 24, 60, 0.08)',
                      },
                    }}
                    aria-label="clear search"
                  >
                    <ClearIcon sx={{ fontSize: 18, color: INACTIVE_COLOR }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            InputLabelProps={{
              sx: {
                color: searchTerm ? ACTIVE_COLOR : INACTIVE_COLOR,
                "&.Mui-focused": {
                  color: ACTIVE_COLOR,
                },
                "&.MuiInputLabel-shrink": {
                  color: ACTIVE_COLOR,
                },
                "&:hover": {
                  color: ACTIVE_COLOR,
                },
              },
            }}
            sx={{
              minWidth: 350,
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
                "&:hover fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
              },
            }}
          />
        </Box>

        <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: "100%", overflowX: "auto", borderRadius: 0 }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: "#f2f2f2" }}>
              <TableRow>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('id')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    ID
                    <Tooltip title="Click to sort">{getSortIcon('id')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('name')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Name
                    <Tooltip title="Click to sort">{getSortIcon('name')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('needUsers')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Need Users
                    <Tooltip title="Click to sort">{getSortIcon('needUsers')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('type')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Type
                    <Tooltip title="Click to sort">{getSortIcon('type')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('custNum')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Cust #
                    <Tooltip title="Click to sort">{getSortIcon('custNum')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('spNum')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    SP #
                    <Tooltip title="Click to sort">{getSortIcon('spNum')}</Tooltip>
                  </Box>
                </TableCell>
                <TableCell
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#e0e0e0' }, userSelect: 'none' }}
                  onClick={() => handleSort('verifyDate')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    Verify Date
                    <Tooltip title="Click to sort">{getSortIcon('verifyDate')}</Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {
                isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Skeleton width="38%" />
                      <Skeleton width="100%" />
                      <Skeleton width="100%" />
                      <Skeleton width="61%" />
                    </TableCell>
                  </TableRow>
                ) : hasFetched && sortedCP.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                ) : sortedCP.map((CPlist: CPlist, idx: number) => (
                  <TableRow key={CPlist.id} hover>
                    <TableCell>
                      <Typography
                        onClick={() => navigate(`/otherextranet/managecp/cp-details/${CPlist.id}`)}
                        sx={{
                          color: '#c3183c',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          '&:hover': {
                            color: '#a01530'
                          }
                        }}
                      >
                        {CPlist.id}
                      </Typography>
                    </TableCell>
                    <TableCell>{CPlist.name}</TableCell>
                    <TableCell>{String(CPlist.needUsers)}</TableCell>
                    <TableCell>{CPlist.type}</TableCell>
                    <TableCell>{CPlist.custNum}</TableCell>
                    <TableCell>{CPlist.spNum}</TableCell>
                    <TableCell>{moment(CPlist.verifyDate).format('MM/DD/YYYY')}</TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
          </Table>
        </TableContainer>

        {/* Back Button (removed pagination) */}
        <Box sx={{ mt: 3, display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#c3183c", padding: "6px 25px !important" }}
            onClick={() => navigate(-1)}
          >
            BACK
          </Button>
        </Box>
      </Box>
    </DashboardLayout>
  );
};

export default ManageChannelPartner;
